import './DodFilterTypePicker.scss';
import React, {Fragment, ReactNode, useImperativeHandle, useMemo, useState} from "react";
import classnames from "classnames";
import {ByzzerButton, ByzzerChangeEventHandler, ByzzerMessagePopUp, ByzzerTipIcon} from '@byzzer/ui-components';
import {nanoid} from "nanoid";
import {useDodService} from "@/services/dodPresets.service";
import {confirm, openModal} from "@/components/form";
import {DodPreset, DodPresetSummary, DodPresetType} from '@/types/ApiTypes';
import {DodPanel} from "@/components/DodConfigEditor/common/DodPanel";
import {cloneDeep, uniq} from "lodash";
import {useDodWizard} from "@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext";
import { getRegexForSearchString } from '@/utils';
import { isWorkInProgressFact, getFilterKey, extractTypeValue, isCharacteristicFilter, isPpgFilter, isMarketResetRequired, isTimePeriodFilterTypes } from '../utils';
import { DodFilter, DodFilters } from '@/types/DodRun';
import closeIcon from '@images/icons/niq-close-white.svg';
import { DodTimePeriodFilterType, DodTimePeriodFilterTypes, DodTimePeriodFilterTypeValueMap } from '../../types';
import { useMarketService } from '@/services/market.service';
import { useDodTimePeriodService } from '@/services/dodTimePeriod.service';
import { DodValueOption } from '../DodFilterValuePicker';
import { RunConfigMarket, TimePeriod } from '@/types/ReportRun';
import {alert} from '@/components/form';
import { useCategoryService } from '@/services/category.service';

const baseClassName = 'dod-filter-value-type-picker';

type FilterValueConfig = {
    getOptions(values: DodFilters, data: any): Promise<DodValueOption[]> | DodValueOption[];
    valueToStrings?(value: TimePeriod[]): string[];
    stringsToValue?(strings: string[]): any;
};

type LoadOptionsFunction<FiltersType, FilterTypeType> =
    (filtersConfig: FiltersType) => Promise<DodFilterTypeOption<FilterTypeType>[]> | DodFilterTypeOption<FilterTypeType>[];

export type DodFilterTypeOptionGroup<FiltersType, FilterTypeType> = {
    key?: string;
    label: ReactNode;
    tip?: ReactNode;
    /**
     Used for filtering matching the group.
     This must be defined if the label isn't a string or filtering will not work.
     */
    filterText?: string;
    options?: DodFilterTypeOption<FilterTypeType>[];
    loadOptions?: LoadOptionsFunction<FiltersType, FilterTypeType>;
    disabled?(filtersConfig?: FiltersType): Promise<boolean> | boolean;
    disabledTip?: ReactNode;
    delete?(filterVal: FilterTypeType): Promise<void> | void;
}

type ExpandableDodFilterTypeOptionGroup<FiltersType, FilterTypeType> =
    DodFilterTypeOptionGroup<FiltersType, FilterTypeType> & {
    matchingOptions?: DodFilterTypeOption<FilterTypeType>[];
    expanded: boolean;
    loading?: boolean;
};

export type DodFilterTypeOption<FilterTypeType> = {
    key?: string;
    label: ReactNode;
    tip?: ReactNode;
    value: FilterTypeType;
    /**
     * Used for filtering matching the option.
     * This must be defined if the label isn't a string or filtering will not work.
     */
    filterText?: string;
    disabled?: (filtersConfig?: any) => Promise<boolean> | boolean,
    disabledTip?: ReactNode;
}

export type DodFilterTypePickerRef = {
    reloadOptionGroups(): void;
    reloadOptionGroupsByKeys(keys: string[], force?: boolean): void;
    reloadOptionGroupsByIndexes(indexes: number[]): void;
}

/**
 * @defaultExpandAll 
 * if set to true, all available options folders will be collapsed
 * if false, existing functionality
 */
export type DodFilterTypePickerProps<FiltersType, FilterTypeType> = {
    name?: string;
    className?: string;
    filters?: FiltersType;
    children?: ReactNode | ReactNode[];
    initialOptionGroups: DodFilterTypeOptionGroup<FiltersType, FilterTypeType>[];
    value: FilterTypeType;
    onChange: ByzzerChangeEventHandler<FilterTypeType>;
    onSelectionChange?: (e: ByzzerChangeEvent<string[] | 'all'>, fieldType?: any) => void;
    onClearSelection?: ByzzerChangeEventHandler<DodFilters>;
    tipDelay?: number | [number | null, number | null];
    pickerRef?: React.Ref<DodFilterTypePickerRef>;
    valueMatcher?(valueA: FilterTypeType, valueB: FilterTypeType): boolean;
    title?: string;
    presetType?: DodPresetType;
    presetGroupLabel?: string;
    presetGroupTip?: ReactNode;
    onPresetSelect: ByzzerChangeEventHandler<DodPreset>;
    filterPlaceholder?: string;
    showPresetWarning?: boolean;
    onValidatePreset?: (preset: DodPreset) => boolean;
    defaultExpandAll?: boolean;
    showAllButton?: boolean;
    confirmMarketResetFn?: (marketNodes?: MarketNode[]) => Promise<boolean>;
    resetFactSelections?(filterType: string): void;
    dodWizardStep?: DodPresetType;
};


const DEFAULT_TIP_DELAY: [number, number] = [500, 0]

function defaultValueMatcher(a: any, b: any): boolean {
    return a === b;
}


export function DodFilterTypePicker<FiltersType, FilterTypeType>({
                                                                     className,
                                                                     name,
                                                                     initialOptionGroups,
                                                                     tipDelay = DEFAULT_TIP_DELAY,
                                                                     value,
                                                                     onChange,
                                                                     filters,
                                                                     pickerRef,
                                                                     title,
                                                                     valueMatcher = defaultValueMatcher,
                                                                     presetType,
                                                                     presetGroupLabel,
                                                                     presetGroupTip,
                                                                     onPresetSelect,
                                                                     showPresetWarning = false,
                                                                     onValidatePreset,
                                                                     onSelectionChange,
                                                                     onClearSelection,
                                                                     defaultExpandAll = false,
                                                                     showAllButton,
                                                                     confirmMarketResetFn,
                                                                     resetFactSelections,
                                                                     dodWizardStep,
                                                                     ...props
                                                                 }: DodFilterTypePickerProps<FiltersType, FilterTypeType>) {

    const {dodPresetsMap, deletePreset, getPreset} = useDodService();
    const {setPreset, runConfig, allSelectionState, setAllSelectionState, allExceptSelectionFields, removeAllExceptSelectionField, deletePreset: removePreset} = useDodWizard();
    const [filterText, setFilterText] = useState<string>('');
    const [savedSelectionsExpanded, setSavedSelectionsExpanded] = useState<boolean>(false);
    const { getCachedMarketNodeByName, dedupMarkets, getEnabledMarkets, getRunConfigMarketForMarketName } = useMarketService();
    const { getAllCategoriesByDepartmentsAndSuperCategories } = useCategoryService();
    const [optionGroups, setOptionGroups] = useState<ExpandableDodFilterTypeOptionGroup<FiltersType, FilterTypeType>[]>(
        initialOptionGroups?.map((group, index) => {

            const options = group.options?.map(option => ({
                ...option,
                key: option.key ?? nanoid(5),
                filterText: option.filterText ?? option.label as string
            }));
            const firstHasOptions = Boolean(initialOptionGroups[0].options);
            return {
                ...group,
                options,
                filterText: group.filterText ?? group.label as string,
                key: group.key ?? nanoid(5),
                expanded: defaultExpandAll ? Boolean(options) :  index === 0 ? firstHasOptions : !firstHasOptions && Boolean(options),
                matchingOptions: options
            }
        })
    );
    const noFilterApplied: boolean = !filterText;
    const noMatches: boolean = Boolean(filterText) && !optionGroups.some(config => Boolean(config.matchingOptions?.length));

    const {
        latestDates,
        weekEndingDates,
        fourWeeksEndingDates,
        yearEndingDates,
        monthEndingDates,
        quarterEndingDates,
        fourWeeksStaticEndingDates,
        previousPeriodDates
    } = useDodTimePeriodService();

    const filterValueConfigs = useMemo<Record<DodTimePeriodFilterType, FilterValueConfig>>(
        () => ({
            latestDates: {
                getOptions(filters) {
                    return latestDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return latestDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            previousPeriods: {
                getOptions(filters) {
                    return previousPeriodDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return previousPeriodDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            weeks: {
                getOptions(filters) {
                    return weekEndingDates;
                    // ['1w:1:1', '1w:1:2']
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return weekEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            '4weeks': {
                getOptions(filters) {
                    return fourWeeksEndingDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return fourWeeksEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            '4StaticWeeks': {
                getOptions(filters) {
                    return fourWeeksStaticEndingDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return fourWeeksStaticEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            months: {
                getOptions(filters) {
                    return monthEndingDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return monthEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            quarters: {
                getOptions(filters) {
                    return quarterEndingDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return quarterEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            years: {
                getOptions(filters) {
                    return yearEndingDates;
                },
                stringsToValue(value: string[] | 'all') {
                    if(value === 'all') {
                        return yearEndingDates.map(val => val.value);
                    } else {
                        return value
                    }
                }
            },
            custom: {
                getOptions(filters) {
                    return [];
                },
            },
            saved: {
                async getOptions() {
                    return [];
                },
            },
        }),
        [filters]
    );

    function removeMarketPreset() {
        removePreset('market');
    }

    const handleAllSelectionToggle = async (type, value, buttonLabel) => {

        const isSelectingAll = buttonLabel === '+All';

        if (isSelectingAll) {
            dodWizardStep && removePreset(dodWizardStep);
            if(isTimePeriodFilterTypes(type as DodTimePeriodFilterTypes)) {
                const { stringsToValue } = filterValueConfigs[type];
                const oldValue = [...(filters?.['timePeriods'].values ?? [])].filter(
                    (val) => val.slice(0, 2) !== DodTimePeriodFilterTypeValueMap[type]
                );
                onSelectionChange?.({
                    name,
                    value: uniq([...oldValue, ...stringsToValue('all')]),
                });
            } else {
                if (value.type === 'customCharacteristics') {
                    alert({
                        title: 'FYI',
                        content: (
                            <>
                                <p>Including Custom Characteristics will slow down the Data Point counter.</p>
                                <p>If it's calculating for longer than usual, that's why.</p>
                            </>
                        ),
                    });
                }
                
                onSelectionChange?.(
                    {
                        name,
                        value: 'all'
                    }, value 
                )
    
                const isExcept =
                    allExceptSelectionFields.includes(value?.data?.id ?? '') ||
                    allExceptSelectionFields.includes(value.type!);
    
                    isExcept && removeAllExceptSelectionField(value?.data?.id || value.type);
            }
        } else {
            if (!filters) return;
            let updatedValues = cloneDeep(filters!);

            if (isCharacteristicFilter(value) || isPpgFilter(value)) {
                const id = extractTypeValue(type);

                if (id !== null) {
                    updatedValues[value.type][id] = {
                        ...updatedValues[value.type][id],
                        values: []
                    };
                }
                if ( 
                    updatedValues[value.type][id]?.values?.length === 0 && 
                    updatedValues[value.type][id]?.summedSelections?.length === 0 
                ) { 
                    delete updatedValues[value.type][id]; 
                }
            } else {

                if (isTimePeriodFilterTypes(type as DodTimePeriodFilterTypes)) {
                    const timePeriods = updatedValues['timePeriods'];
                    const filterValue = DodTimePeriodFilterTypeValueMap[type || ''];

                    updatedValues['timePeriods'] = {
                        ...timePeriods,
                        values:
                            type === 'timePeriods'
                                ? []
                                : timePeriods.values.reduce((acc: string[], val) => {
                                      if (val.slice(0, 2) !== filterValue) {
                                          acc.push(val as string);
                                      }
                                      return acc;
                                  }, []),
                    };
                } else {
                    const requireMarketUpdate = filters['markets']?.values.length > 0 && type === 'categories';
                    const requiresMarketReset = isMarketResetRequired(value, filters as any);

                    let updatedCategoryFilter = updatedValues['categories'];
                    let dedupAllCategories: string[] = [];

                    if (type === 'categories') {
                        updatedCategoryFilter = {
                            summedSelections: updatedValues['categories'].summedSelections,
                            values:
                                updatedValues['categories'].values === 'all'
                                    ? []
                                    : (updatedValues['categories'].values as string[]).filter((item) => item !== 'all'),
                        };

                        if (
                            updatedCategoryFilter.values === 'all' ||
                            updatedCategoryFilter.summedSelections.some((ss) => ss.values === 'all')
                        ) {
                            dedupAllCategories = getAllCategoriesByDepartmentsAndSuperCategories(
                                updatedValues['departments'],
                                updatedValues['superCategories']
                            );
                        } else {
                            dedupAllCategories = uniq(
                                updatedCategoryFilter.values.concat(
                                    updatedCategoryFilter.summedSelections
                                        .map((sum) => sum.values)
                                        .concat(updatedCategoryFilter.values)
                                        .flat()
                                )
                            );
                        }
                    }

                    const summedCategories: string[] = updatedCategoryFilter.summedSelections.some(
                        (ss) => ss.values === 'all'
                    )
                        ? getAllCategoriesByDepartmentsAndSuperCategories(
                            updatedValues['departments'],
                            updatedValues['superCategories']
                        )
                        : uniq(updatedCategoryFilter.summedSelections.map((sum) => sum.values).flat());

                    const nonSummedCategories: string[] =
                        updatedCategoryFilter.values === 'all'
                            ? getAllCategoriesByDepartmentsAndSuperCategories(
                                updatedValues['departments'],
                                updatedValues['superCategories']
                            )
                            : updatedCategoryFilter.values;

                    const selectedMarkets: RunConfigMarket[] = dedupMarkets(
                        filters['markets']?.values.concat(
                            filters['markets']?.summedSelections.map((sumMrkt) => sumMrkt.values).flat()
                        )
                    );

                    const enabledMarkets: RunConfigMarket[] = getEnabledMarkets(
                        selectedMarkets,
                        dedupAllCategories,
                        true,
                        Boolean(summedCategories.length),
                        summedCategories,
                        nonSummedCategories,
                        false
                    ) as RunConfigMarket[];

                    const validMarkets: string[] = enabledMarkets.map((mrkt) => mrkt.name);
                    const invalidMarkets: string[] = selectedMarkets
                        .filter((mrkt) => !validMarkets.includes(mrkt.name))
                        .map((mrkt) => mrkt.name);

                    const invalidMarketNodes: MarketNode[] = invalidMarkets.map(
                        (mrkt) =>
                            getCachedMarketNodeByName(
                                mrkt,
                                dedupAllCategories,
                                true,
                                Boolean(summedCategories.length),
                                summedCategories,
                                nonSummedCategories,
                                false
                            ) as MarketNode
                    );

                    if (requireMarketUpdate) {
                        // update partially approved markets values for updated categories
                        updatedValues['markets'].values = updatedValues['markets'].values.map(
                            (mrkt) =>
                                getRunConfigMarketForMarketName(
                                    mrkt.name,
                                    dedupAllCategories,
                                    true,
                                    Boolean(summedCategories.length),
                                    summedCategories,
                                    nonSummedCategories,
                                    false
                                ) as RunConfigMarket
                        );
                    }

                    if (
                        confirmMarketResetFn &&
                        requiresMarketReset &&
                        invalidMarketNodes.length &&
                        !(await confirmMarketResetFn(invalidMarketNodes))
                    )
                        return;

                    if (requiresMarketReset) {
                        updatedValues['markets'] = {
                            values: updatedValues['markets'].values.filter((mrkt) => !invalidMarkets.includes(mrkt.name)),
                            summedSelections: updatedValues['markets'].summedSelections.map((sumMrkt) => ({
                                ...sumMrkt,
                                values: sumMrkt.values.filter((mrkt) => !invalidMarkets.includes(mrkt.name)),
                            })),
                        };
                        removeMarketPreset();
                    }

                    if (type === 'upcs' || type === 'brands' || type === 'subcategories') {
                        resetFactSelections?.(type);
                    }

                    // Clear current filter type values
                    updatedValues[value.type] = {
                        ...updatedValues[value.type],
                        values: [],
                    } as DodFilter;
                }
            }
            onClearSelection?.({ value: updatedValues });
        }

        setAllSelectionState(type);
    };

    useImperativeHandle(pickerRef, () => ({
        async reloadOptionGroups() {
            optionGroups.forEach(group => loadOptions(group))
        },
        async reloadOptionGroupsByIndexes(indexes: number[]) {
            indexes.forEach(index => loadOptions(optionGroups[index]));
        },
        async reloadOptionGroupsByKeys(keys: string[], force = false) {
            optionGroups.filter(({key}) => keys.includes(key!)).forEach(group => loadOptions(group, force));
        }
    }), [optionGroups, filters]);

    function toggleSavedSelectionExpansion() {
        setSavedSelectionsExpanded(value => !value);
    }

    async function handleGroupClick(group: ExpandableDodFilterTypeOptionGroup<FiltersType, FilterTypeType>) {

        if (group.disabled?.(filters)) return;

        group.expanded = !group.expanded;
        setOptionGroups(groups => groups.map(config => ({
            ...config,
            expanded: config.key === group.key ? group.expanded : config.expanded
        })));

        if (!group.options) {
            loadOptions(group);
        }
    }

    function handleOptionClick(node: DodFilterTypeOption<FilterTypeType>) {
        onChange({
            name,
            value: node.value!
        });
    }

    function handleFilterChange(value) {
        const matcher = getRegexForSearchString(value);
        setOptionGroups(state => state.map(config => ({
            ...config,
            matchingOptions: config.options?.filter(({filterText}) => matcher.test(filterText!))
        })))
    }

    function clearOptions(group: DodFilterTypeOptionGroup<FiltersType, FilterTypeType>) {

        setOptionGroups(groups => groups.map(config => ({
            ...config,
            options: config.key === group.key ? undefined : config.options,
            matchingOptions: config.key === group.key ? undefined : config.matchingOptions
        })));
    }

    async function loadOptions(group: ExpandableDodFilterTypeOptionGroup<FiltersType, FilterTypeType>, force: boolean = false) {
        if (!group.loadOptions) return;

        // clear the options, this will cause the options to reload the next time the group is expanded
        clearOptions(group);

        // only actively reload an expanded node, since we cleared the options above the node will be reloaded the
        // nex time it's expanded.
        if (!group.expanded && force !== true) return;

        setOptionGroups(groups => groups.map(g => ({
            ...g,
            loading: g.key === group.key ? true : g.loading,
        })));
        const options = (await group.loadOptions(filters!)).map(option => ({
            ...option,
            key: option.key ?? nanoid(5),
            filterText: option.filterText ?? option.label as string
        }));
        setOptionGroups(groups => groups.map(g => {

            if (g.key !== group.key) return g;

            return {
                ...g,
                options,
                matchingOptions: options,
                loading: false
            }
        }));
    }

    async function handleSavedSelectionClick(preset: DodPresetSummary | DodPreset): Promise<void> {
        const dodPreset = (preset as DodPreset).values ? cloneDeep(preset as DodPreset) : await getPreset(preset.id, runConfig);
        if(onValidatePreset && !onValidatePreset(dodPreset)) {
            return
        }
        if (!showPresetWarning || await confirm({
            title: 'Warning',
            content: <>
                <p>Applying this Saved Selection will replace any selections you've already made on this step.</p>
                <p>Do you want to continue?</p>
            </>,
            yesLabel: 'Yes',
            noLabel: 'No'
        })) {
            setPreset(presetType!, preset);
            onPresetSelect?.({
                name,
                value: dodPreset
            });
        }
    }

    async function handleSavedSelectionDelete(id: number): Promise<void> {

        openModal({
            title: 'Delete Saved Selection',
            showCloseOption: false,
            content: ({busy}) => {

                return <>
                    {busy ? (
                        <p>Removing Saved Selection...</p>
                    ) : (
                        <p>Are you sure you want to delete this Saved Selection?</p>
                    )}
                </>
            },
            actions: [{
                key: 'cancel',
                label: 'No, Cancel',
                type: 'negative',
                action({reject}) {
                    reject()
                }
            }, {
                key: 'yes',
                label: 'Yes',
                async action({resolve, setBusy}) {
                    try {
                        setBusy(true)
                        await deletePreset(id);
                        resolve(true)
                    } finally {
                        setBusy(false);
                    }
                }
            }]
        })
    }

    //TODO: remove after QA testing
    const disabledSelections = ['ppgs', 'characteristics', 'customCharacteristics'];
    const disabledByInProgressFacts = useMemo(() => {
        return isWorkInProgressFact(runConfig.facts);
    }, [runConfig.facts]);


    return (
        <DodPanel
            className={classnames(baseClassName, className)}
            {...props}
            title={title}
            emptyState={'No Matching Filters Found'}
            isEmpty={noMatches}
            onFilterChange={handleFilterChange}
            trackClick={{ data: { dodWizardStep: presetType, panel: 'value picker' } }}
        >
            <div className={`${baseClassName}__tree`}>
                {optionGroups.map((group) => {
                    return (
                        <React.Fragment key={`option-group-${group.key}`}>
                            {Boolean(noFilterApplied || group.matchingOptions?.length) && (
                                <div
                                    onClick={() => {
                                        if (group.disabled?.(filters)) return;
                                        handleGroupClick(group);
                                    }}
                                    className={classnames(`${baseClassName}__node`, `${baseClassName}__section-node`, {
                                        [`${baseClassName}__section-node--expanded`]: group.expanded,
                                        [`${baseClassName}__section-node--loading`]: group.loading,
                                        [`${baseClassName}__section-node--disabled`]:
                                            group.disabled?.(filters),
                                    })}
                                >
                                    <ByzzerMessagePopUp
                                        tipTrigger={'hover'}
                                        tipDelay={tipDelay}
                                        tip={group.disabledTip}
                                    >
                                        {group.label}
                                    </ByzzerMessagePopUp>

                                    {group.tip && (
                                        <ByzzerTipIcon
                                            tipDelay={tipDelay}
                                            tip={<div className={`${baseClassName}__tip`}>{group.tip}</div>}
                                        />
                                    )}
                                </div>
                            )}
                            {group.expanded &&
                                group.matchingOptions?.map((option: any) => {
                                    const isSelected = !!allSelectionState[getFilterKey(option)];
                                    const buttonLabel = isSelected ? <span>All <img className={`${baseClassName}__close-icon`} src={closeIcon} alt='Close'></img></span> : '+All';

                                    return (
                                        <Fragment key={`filter-option-${option.key}`}>
                                        <div
                                            className={classnames(
                                                `${baseClassName}__node`,
                                                `${baseClassName}__value-node`,
                                                {
                                                    [`${baseClassName}__value-node--selected`]: valueMatcher(
                                                        option.value,
                                                        value
                                                    ),
                                                    [`${baseClassName}__value-node--disabled`]: option?.disabled?.(
                                                        filters
                                                    ),
                                                    [`${baseClassName}__value-node--truncated`]: isSelected,
                                                }
                                            )}
                                            key={getFilterKey(option)}
                                        >
                                            <ByzzerMessagePopUp
                                                disabled={!option.disabled?.(filters)}
                                                tipTrigger={'hover'}
                                                tipDelay={tipDelay}
                                                tipLocation="right"
                                                tip={group.disabledTip}
                                            >
                                                <span onClick={() => handleOptionClick(option)}>{option.label}</span>
                                            </ByzzerMessagePopUp>

                                            {!option.disabled?.(filters) && showAllButton && option.value !== 'custom' && (
                                                <ByzzerButton
                                                    type={isSelected ? 'default' : 'negative'}
                                                    className={classnames(`${baseClassName}__plus-button`,{
                                                        [`${baseClassName}__plus-button--selected`]: isSelected
                                                    })}
                                                    onClick={() => handleAllSelectionToggle(getFilterKey(option), option.value, buttonLabel)}
                                                >{buttonLabel}</ByzzerButton>
                                            )}

                                            {option.tip && <ByzzerTipIcon tip={option.tip} tipDelay={tipDelay} />}
                                            {group.delete && (
                                                <span
                                                    className={`${baseClassName}__value-node--delete`}
                                                    onClick={() => group?.delete?.(option.value)}
                                                ></span>
                                            )}
                                        </div>
                                    </Fragment>
                                    )})}
                            {/* This will applicable for all folders, if this is not the correct way then we need to fix in other way  */}
                            {group.expanded && !group.matchingOptions?.length && (
                                <div className={`${baseClassName}__empty`}>No Available Options</div>
                            )}
                        </React.Fragment>
                    );
                })}
                {Boolean(presetType) && (
                    <>
                        <div
                            onClick={() => toggleSavedSelectionExpansion()}
                            className={classnames(`${baseClassName}__node`, `${baseClassName}__section-node`, {
                                [`${baseClassName}__section-node--expanded`]: savedSelectionsExpanded,
                            })}
                        >
                            {presetGroupLabel}
                            {Boolean(presetGroupTip) && (
                                <ByzzerTipIcon
                                    tipDelay={tipDelay}
                                    tip={<div className={`${baseClassName}__tip`}>{presetGroupTip}</div>}
                                />
                            )}
                        </div>
                        {savedSelectionsExpanded &&
                            dodPresetsMap[presetType!]?.map((preset) => (
                                <div
                                    key={`saved-selection-${preset.id}`}
                                    className={classnames(`${baseClassName}__node`, `${baseClassName}__value-node`)}
                                >
                                    <span onClick={() => handleSavedSelectionClick(preset)}>{preset.displayName}</span>

                                    {/* assume id <= 0 are don't exist on the server so they can't be deleted*/}
                                    {preset.id > 0 && (
                                        <i
                                            className={`${baseClassName}__value-node--delete`}
                                            onClick={() => handleSavedSelectionDelete(preset.id)}
                                        />
                                    )}
                                </div>
                            ))}
                    </>
                )}
            </div>
        </DodPanel>
    );
}

export default DodFilterTypePicker;